// Parses the PDF417 barcode payload printed on the back of a US driver's license (AAMVA
// format) into the same 12-key shape the backend's vision fallback and license-prefill
// endpoint use. Ported from the compliance portal's onboarding wizard.

const LIC_FIELD_KEYS = [
  "legal_first", "legal_middle", "legal_last", "dob",
  "addr_street", "addr_city", "addr_state", "addr_zip",
  "license_number", "license_class", "license_state", "license_expiry",
];

function element(raw, code) {
  const m = raw.match(new RegExp("(?:^|[\\r\\n]|DL)" + code + "([^\\r\\n]*)"));
  return m ? m[1].trim() : "";
}

// AAMVA dates arrive as either MMDDCCYY (older cards) or CCYYMMDD (newer cards); tell them
// apart by whether the first 4 digits look like a plausible year.
function toIsoDate(s) {
  s = (s || "").trim();
  if (!/^\d{8}$/.test(s)) return "";
  const first4 = Number(s.slice(0, 4));
  if (first4 > 1900 && first4 < 2100) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return `${s.slice(4, 8)}-${s.slice(0, 2)}-${s.slice(2, 4)}`;
}

// Splits a single full-name barcode element (DAA, used by older cards that never split the
// name into DCS/DAC/DAD) using the same name-order rule the portal applies everywhere else:
// the family name stands on its own, the first token of the given names is the first name,
// anything left over is the middle name. AAMVA's own DAA convention is family name first.
function splitFullName(full) {
  const commaParts = full.split(/[,$]/).map((p) => p.trim()).filter(Boolean);
  if (commaParts.length >= 2) {
    return { last: commaParts[0], first: commaParts[1], middle: commaParts.slice(2).join(" ") };
  }
  const words = full.split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return { last: words[0], first: words[1], middle: words.slice(2).join(" ") };
  }
  if (words.length === 1) {
    return { last: words[0], first: "", middle: "" };
  }
  return { last: "", first: "", middle: "" };
}

// parseAAMVA(raw) -> the 12-key fields object, or null when nothing recognizable was found.
export function parseAAMVA(raw) {
  raw = String(raw || "");

  let legalFirst = element(raw, "DAC");
  let legalMiddle = element(raw, "DAD");
  let legalLast = element(raw, "DCS") || element(raw, "DAB");

  if (!legalFirst && !legalLast) {
    const full = element(raw, "DAA");
    if (full) {
      const split = splitFullName(full);
      legalLast = split.last;
      legalFirst = split.first;
      legalMiddle = split.middle;
    }
  }

  const fields = {
    legal_first: legalFirst,
    legal_middle: legalMiddle,
    legal_last: legalLast,
    dob: toIsoDate(element(raw, "DBB")),
    addr_street: element(raw, "DAG"),
    addr_city: element(raw, "DAI"),
    addr_state: element(raw, "DAJ"),
    addr_zip: element(raw, "DAK").replace(/[^0-9]/g, "").slice(0, 5),
    license_number: element(raw, "DAQ"),
    license_class: element(raw, "DCA"),
    license_state: element(raw, "DAJ"),
    license_expiry: toIsoDate(element(raw, "DBA")),
  };

  return (fields.legal_last || fields.legal_first || fields.license_number) ? fields : null;
}

function shiftYears(d, years) {
  const copy = new Date(d.getTime());
  copy.setFullYear(copy.getFullYear() + years);
  return copy;
}

function parseIsoDate(s) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s || "")) return null;
  const d = new Date(`${s}T00:00:00`);
  return isNaN(d.getTime()) ? null : d;
}

// licenseGuards(fields) -> [{field, message}], the same rules the backend enforces on
// PUT /drivers/{did}/license-prefill, so the review form can warn before the driver saves.
export function licenseGuards(fields) {
  const issues = [];
  const add = (field, message) => issues.push({ field, message });
  const f = fields || {};

  if (!(f.legal_first || "").trim()) add("legal_first", "First name is required");
  if (!(f.legal_last || "").trim()) add("legal_last", "Last name is required");

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (f.dob) {
    const d = parseIsoDate(f.dob);
    if (!d) add("dob", "Date of birth must be a valid date");
    else if (d > shiftYears(today, -16)) add("dob", "Driver must be at least 16 years old");
  }

  if (f.license_expiry) {
    const e = parseIsoDate(f.license_expiry);
    if (!e) add("license_expiry", "Expiration date must be a valid date");
    else {
      if (e < today) add("license_expiry", "Expiration date is in the past, check the license");
      if (e > shiftYears(today, 10)) add("license_expiry", "Expiration date is more than 10 years out, check the license");
    }
  }

  ["addr_state", "license_state"].forEach((field) => {
    const v = (f[field] || "").trim();
    if (v && !/^[A-Za-z]{2}$/.test(v)) add(field, "Must be a 2-letter state code");
  });

  const zip = (f.addr_zip || "").trim();
  if (zip && !/^\d{5}$/.test(zip)) add("addr_zip", "ZIP must be 5 digits");

  return issues;
}

export { LIC_FIELD_KEYS };
